import { CompletedShipmentsContext } from "@/components/CompletedShipmentStat"
import {
  getShipmentConcept,
  normalizeShipmentPath,
} from "@/lib/completed-shipments"
import "../styles/global.css"

import { useEffect } from "react"
import dynamic from "next/dynamic"
import Error from "next/error"
import { ErrorBoundary } from "react-error-boundary"
import type { AppProps } from "next/app"
import { Anchor } from "../app/components/anchor"
import { useRouter } from "next/router"
import {
  Montserrat,
  Roboto,
  Merriweather,
  Inter,
  Work_Sans,
} from "next/font/google"
import store from "store"

import Head from "next/head"
import { PostHogProvider } from "../util/posthog/posthogProvider"
import { captureError } from "../util/posthog/capture-error"
import { GeoDataProvider } from "../context/GeoDataContext"
import { homeSchema } from "../seo/schemas"
import { NavigationProgress } from "../components/navigation-progress"
import { env } from "../config"
import { isLiveChatRoute } from "../components/live-chat-shared"

const WebMCPProvider = dynamic(
  () =>
    import("../components/webmcp/WebMCPProvider").then(
      (mod) => mod.WebMCPProvider,
    ),
  { ssr: false },
)

const LiveChat = dynamic(
  () => import("../components/live-chat").then((module) => module.LiveChat),
  { ssr: false },
)

// Montserrat/Roboto don't render in the homepage's above-the-fold content —
// skipping preload keeps their woff2 files off its LCP critical path. They do
// style ATF text elsewhere (PageNav links, programmatic-page h1s); the
// accepted cost there is a slightly longer font-swap window, not LCP — see
// docs/homepage-performance-plan.md §8.
const montserrat = Montserrat({
  variable: "--font-montserrat",
  subsets: ["latin"],
  preload: false,
})

const roboto = Roboto({
  variable: "--font-roboto",
  weight: ["100", "300", "400", "500", "700", "900"],
  subsets: ["latin"],
  preload: false,
})

const inter = Inter({
  variable: "--font-inter",
  subsets: ["latin"],
  display: "swap",
})

const merriweather = Merriweather({
  variable: "--font-merriweather",
  weight: ["300", "400", "700", "900"],
  subsets: ["latin"],
  display: "swap",
})
const work_sans = Work_Sans({
  variable: "--font-work_sans",
  subsets: ["latin"],
  display: "swap",
})
const className = `
  ${montserrat.variable} ${montserrat.className} 
  ${roboto.variable} ${roboto.className} 
  ${merriweather.variable} ${merriweather.className}
  ${inter.variable} ${inter.className}
  ${work_sans.variable} ${work_sans.className}
`.trim()

// `error` is typed `unknown` because `Error` here is the next/error component.
function reportBoundaryError(
  error: unknown,
  info: { componentStack: string },
): void {
  captureError(error, {
    source: "app_error_boundary",
    component_stack: info.componentStack,
  })
}

function reportLiveChatError(
  error: unknown,
  info: { componentStack: string },
): void {
  captureError(error, {
    source: "live_chat_error_boundary",
    component_stack: info.componentStack,
  })
}

export default function MyApp({ Component, pageProps }: AppProps) {
  const { pathname, asPath } = useRouter()
  const showLiveChat =
    env("FF_LIVE_CHAT_ENABLED") === "1" && isLiveChatRoute(asPath)
  const shouldRenderHomeSchema = pageProps?.isHomePage === true
  // `key` in pageProps is reserved to force a page remount when the Storyblok
  // story changes (returned from getStaticProps in [[...slug]].tsx). Pull it out
  // so it's passed directly to <Component> instead of spread into JSX.
  const { key: pageKey, ...restPageProps } = pageProps ?? {}

  useEffect(() => {
    store.set("last_visited_page", { name: pathname })
  }, [])

  return (
    <>
      <NavigationProgress />
      <Head>{shouldRenderHomeSchema ? homeSchema : null}</Head>
      {/* PostHogProvider sits above the boundary so a page crash cannot unmount
          it before init, which would drop the boundary's error report. */}
      <PostHogProvider>
        <ErrorBoundary
          fallbackRender={() => null}
          onError={reportLiveChatError}
          resetKeys={[asPath]}
        >
          {showLiveChat && <LiveChat />}
        </ErrorBoundary>
        <ErrorBoundary
          FallbackComponent={() => <Error statusCode={404} />}
          onError={reportBoundaryError}
          resetKeys={[asPath]}
        >
          {restPageProps.preview && (
            <header className="z-[1000] fixed bg-black text-white font-bold w-full bottom-0 h-10 text-center flex justify-between items-center px-10 font-mono">
              Preview Mode Enabled
              <Anchor
                href="/api/exit-preview"
                prefetch={false}
                className="text-white underline"
              >
                Exit Preview
              </Anchor>
            </header>
          )}
          <main className={className}>
            <GeoDataProvider>
              <CompletedShipmentsContext.Provider
                value={{
                  data: restPageProps.completedShipments ?? null,
                  pagePath: normalizeShipmentPath(
                    restPageProps.storySlug ?? asPath,
                  ),
                  concept: getShipmentConcept(
                    restPageProps.storySlug ?? asPath,
                  ),
                }}
              >
                <Component key={pageKey} {...restPageProps} />
              </CompletedShipmentsContext.Provider>
            </GeoDataProvider>
            <WebMCPProvider />
          </main>
        </ErrorBoundary>
      </PostHogProvider>
    </>
  )
}
